import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import anime from 'animejs/lib/anime.es.js';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronRight, Play } from 'lucide-react';

export default function EventsPage() {
  const { t } = useLanguage();
  const heroRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 900);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    document.title = 'Events // VVS';
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !heroRef.current) return;

    anime.timeline({ easing: 'easeOutExpo' })
      .add({ targets: '.ev-card', translateY: [40, 0], opacity: [0, 1], duration: 800, delay: anime.stagger(150) });
  }, []);

  return (
    <div style={{ position: 'relative', zIndex: 1, paddingTop: '80px', minHeight: '100vh' }}>
      <div ref={heroRef} style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: isMobile ? '40px 20px 80px' : '60px 48px 100px',
      }}>
        {/* Page title */}
        <h1 style={{
          fontFamily: 'Syne, sans-serif',
          fontWeight: 800,
          fontSize: isMobile ? '36px' : 'clamp(42px, 6vw, 56px)',
          textTransform: 'uppercase',
          letterSpacing: '-0.02em',
          color: '#F0F4F8',
          marginBottom: '48px',
        }}>
          Events
        </h1>

        {/* Two cards side by side */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '24px',
        }}>
          {/* Card 1 - WITHIN (Last Event) */}
          <div 
            className="ev-card"
            style={{
              background: 'rgba(10, 22, 40, 0.6)',
              border: '1px solid rgba(240,244,248,0.08)',
              borderRadius: '8px',
              padding: isMobile ? '28px' : '36px',
              opacity: 0,
            }}
          >
            <p style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: '11px',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'var(--vvs-cyan)',
              marginBottom: '16px',
            }}>
              {t('events.last')}
            </p>

            <h2 style={{
              fontFamily: 'Syne, sans-serif',
              fontWeight: 800,
              fontSize: isMobile ? '32px' : '42px',
              textTransform: 'uppercase',
              letterSpacing: '-0.02em',
              color: '#F0F4F8',
              marginBottom: '8px',
            }}>
              WITHIN
            </h2>

            <p style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: '13px',
              letterSpacing: '0.08em',
              color: 'var(--vvs-cyan)',
              marginBottom: '12px',
            }}>
              360° Immersive Experience
            </p>

            <p style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: '14px',
              color: 'var(--vvs-muted)',
              marginBottom: '20px',
              lineHeight: 1.6,
            }}>
              26.02.2026 // Brussels Planetarium
            </p>

            <p style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: '13px',
              color: 'var(--vvs-steel)',
              marginBottom: '28px',
              lineHeight: 1.7,
            }}>
              Under the dome, sound became architecture.
            </p>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Link 
                to="/events/within" 
                className="btn-metallic"
                data-testid="within-details-btn"
                style={{ 
                  textDecoration: 'none', 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '8px',
                }}
              >
                Discover <ChevronRight size={14} />
              </Link>
              <Link 
                to="/events/within/gallery" 
                className="btn-metallic btn-metallic-filled"
                data-testid="within-gallery-btn"
                style={{ 
                  textDecoration: 'none', 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '8px',
                }}
              >
                View Gallery <ChevronRight size={14} />
              </Link>
            </div>
          </div>

          {/* Card 2 - VVS x Metro Club (New) */}
          <div 
            className="ev-card"
            style={{
              background: 'rgba(10, 22, 40, 0.6)',
              border: '1px solid rgba(240,244,248,0.08)',
              borderRadius: '8px',
              padding: isMobile ? '28px' : '36px',
              opacity: 0,
            }}
          >
            <p style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: '11px',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'var(--vvs-cyan)',
              marginBottom: '16px',
            }}>
              NEW // LIVE SET
            </p>

            <h2 style={{
              fontFamily: 'Syne, sans-serif',
              fontWeight: 800,
              fontSize: isMobile ? '32px' : '42px',
              textTransform: 'uppercase',
              letterSpacing: '-0.02em',
              color: '#F0F4F8',
              marginBottom: '8px',
            }}>
              VVS x METRO CLUB
            </h2>

            <p style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: '13px',
              letterSpacing: '0.08em',
              color: 'var(--vvs-cyan)',
              marginBottom: '12px',
              textTransform: 'lowercase',
            }}>
              new live set each week
            </p>

            <p style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: '14px',
              color: 'var(--vvs-muted)',
              marginBottom: '20px',
              lineHeight: 1.6,
            }}>
              27.06.2025 // Metro Club // Ath, Belgium
            </p>

            <p style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: '13px',
              color: 'var(--vvs-steel)',
              marginBottom: '28px',
              lineHeight: 1.7,
            }}>
              The founding event of Vague Vision Sound.<br />
              25 artists. 2 rooms. B2B only. One vision.
            </p>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Link 
                to="/events/metro-club" 
                className="btn-metallic"
                data-testid="metro-club-discover-btn"
                style={{ 
                  textDecoration: 'none', 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '8px',
                }}
              >
                Discover <ChevronRight size={14} />
              </Link>
              <Link 
                to="/events/metro-club" 
                className="btn-metallic btn-metallic-filled"
                data-testid="metro-club-btn"
                style={{ 
                  textDecoration: 'none', 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '8px',
                }}
              >
                <Play size={14} /> Watch Now <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
